import { darkColors, lightColors } from "@/src/theme/tokens";
import type { ModeColorTokens } from "@/src/theme/tokens";

/**
 * Attribute `InitColorSchemeScript` writes on `<html>` before the first paint.
 *
 * Matches MUI's default so the script needs no `attribute` prop. The theme
 * provider keeps it in step with the reader's choice from then on.
 */
export const COLOR_SCHEME_ATTRIBUTE = "data-mui-color-scheme";

const schemeRules = (
  scheme: "light" | "dark",
  colors: ModeColorTokens
): string => `
  html[${COLOR_SCHEME_ATTRIBUTE}="${scheme}"],
  html[${COLOR_SCHEME_ATTRIBUTE}="${scheme}"] body {
    background-color: ${colors.background.default};
    color: ${colors.text.primary};
    color-scheme: ${scheme};
  }
`;

/**
 * Document colors for the window between the first paint and React.
 *
 * The app runs as an SPA (`ssr: false`), so nothing paints the shell until the
 * bundle has loaded, the theme provider has mounted and Emotion has injected
 * `CssBaseline` - long enough for a dark mode reader to get a full white page
 * first. Inlined in `<head>` right after `InitColorSchemeScript`, these rules
 * paint the same colors `CssBaseline` is about to paint, from the attribute the
 * script has already set, so the first frame is correct.
 *
 * Light is spelled out alongside dark for `color-scheme`: it tells the browser
 * which form controls and scrollbars to render, and it has to be unset again
 * when the reader toggles back.
 */
export const colorSchemeBootstrapCss = `${schemeRules("light", lightColors)}${schemeRules("dark", darkColors)}`;
